import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";

import { DeviceContext } from "components/device/DeviceContext";
import { createDevice, updateDevice, validateDevice } from "services/DeviceService";
import { handleError } from "utils/handleError";
import { Dropdown } from "primereact/dropdown";

export const DeviceEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["device", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const deviceContext = useContext(DeviceContext);
    const { device, resetDevice, setDevice, devices, setDevices, submitted, setSubmitted, deviceDialog, setDeviceDialog, appVersions, setAppVersions } = deviceContext;

    const [showSpinner, setShowSpinner] = useState(false);
    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _device = { ...device };
        _device[`${name}`] = val;

        setDevice(_device);
    };

    const onEnabledChange = (e) => {
        let _device = { ...device };
        _device["enabled"] = e.checked;
        setDevice(_device);
    };

    const onMainChange = (e) => {
        let _device = { ...device };
        _device["main"] = e.checked;
        setDevice(_device);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setDeviceDialog(false);
    };

    const saveDeviceAction = () => {
        setSubmitted(true);
        setShowSpinner(true);

        validateDevice(device)
            .then(() => {
                if (!device.id) {
                    // Creating
                    return createDevice(headers, device).then((res) => {
                        let _devices = [...devices];
                        _devices.push(res.data);
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("device") }), life: 3000 });

                        setDevices(_devices);
                        setDeviceDialog(false);
                        resetDevice();
                    });
                } else {
                    // Updating
                    return updateDevice(headers, device.id, {
                        appVersion: device.appVersion,
                        enabled: device.enabled,
                        main: device.main,
                    }).then((res) => {
                        let _devices = [...devices];
                        const index = findIndexById(device.id);
                        _devices[index] = res.data;

                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("device") }), life: 3000 });

                        setDevices(_devices);
                        setDeviceDialog(false);
                        resetDevice();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < devices.length; i++) {
            if (devices[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveDeviceAction} />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={deviceDialog} style={{ width: "70%" }} header={t("device.titleDetail")?.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                {/* tên đăng nhập */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="username" style={{ fontWeight: "500" }}>
                                        {t("username")}
                                    </label>
                                    <InputText id="username" value={device.username} onChange={(e) => onInputChange(e, "username")} required autoFocus readOnly={device.id} />
                                </div>

                                {/* UDID */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="uniqueId" style={{ fontWeight: "500" }}>
                                        {t("uniqueId")}
                                    </label>
                                    <InputText id="uniqueId" value={device.uniqueId} keyfilter={/[^\s]/} onChange={(e) => onInputChange(e, "uniqueId")} required autoFocus readOnly={device.id} className={classNames({ "p-invalid": submitted && !device.uniqueId })} />
                                </div>

                                {/* kích hoạt */}
                                <div className="field  col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <label htmlFor="enabled" style={{ fontWeight: "500", padding: "0px 5px 0px 0px" }}>
                                        {t("enabled")}
                                    </label>
                                    <Checkbox readOnly id="enabled" checked={device.enabled} onChange={onEnabledChange} />
                                </div>
                            </div>

                            <div className="grid">
                                {/* tên thiết bị */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="name" style={{ fontWeight: "500" }}>
                                        {t("name")}
                                    </label>
                                    <InputText id="name" value={device.name} onChange={(e) => onInputChange(e, "name")} required autoFocus readOnly={device.id} />
                                </div>

                                {/* hệ điệu hành */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="platform" style={{ fontWeight: "500" }}>
                                        {t("platform")}
                                    </label>
                                    <InputText id="platform" value={device.platform} keyfilter={/[^\s]/} onChange={(e) => onInputChange(e, "platform")} required autoFocus readOnly={device.id} />
                                </div>

                                {/* thời gian đăng nhập cuối */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="lastLoginAt" style={{ fontWeight: "500" }}>
                                        {t("lastLoginAt")}
                                    </label>
                                    <InputText id="lastLoginAt" value={device.lastLoginAt} onChange={(e) => onInputChange(e, "lastLoginAt")} required autoFocus readOnly={device.id} />
                                </div>

                                {/* thời gian đăng xuất cuối */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="lastLogoutAt" style={{ fontWeight: "500" }}>
                                        {t("device.lastLogoutAt")}
                                    </label>
                                    <InputText id="lastLogoutAt" value={device.lastLogoutAt} onChange={(e) => onInputChange(e, "lastLogoutAt")} required autoFocus readOnly={device.id} />
                                </div>
                            </div>

                            <div className="grid">
                                {/* phiên bản ứng dụng */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="appVersion" style={{ fontWeight: "500" }}>
                                        {t("appVersion")}
                                    </label>
                                    <Dropdown id="appVersion" value={device.appVersion} options={appVersions} onChange={(e) => onInputChange(e, "appVersion")} disabled={device.id} />
                                </div>
                                {/* thiết bị chính */}
                                <div className="field col-12 md:col-3" style={{ display: "flex", justifyContent: "flex-start", marginTop: "auto", marginBottom: "auto" }}>
                                    <label htmlFor="main" style={{ fontWeight: "500", padding: "0px 5px 0px 0px" }}>
                                        {t("main")}
                                    </label>
                                    <Checkbox readOnly id="main" checked={device.main} onChange={onMainChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid">
                    <div className="col-12">
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
